import React, { useState, useEffect, useCallback } from "react";
import { 
  Box, 
  Card, 
  Grid, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow, 
  TableContainer, 
  Paper,
  Chip,
  Stack,
  styled,
  useTheme,Pagination
} from "@mui/material";
import { fetchHistoricalLiveSummary } from "../../services/historicalDataLiveStatusService";
import { historicalDataLiveStatusSummary } from "../../types/types";
import { Loader } from "../../components/loader/Loader";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';




const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${TableHead}`]: {
    backgroundColor: '#24c6b1',
    color: theme.palette.common.white,
    fontWeight: 600,
    padding: theme.spacing(2),
  },
  [`&.${TableBody}`]: {
    fontSize: 14,
    padding: theme.spacing(1.5),
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
    boxShadow: theme.shadows[2],
    transition: 'all 0.3s ease',
  },
}));

const StatusBadge = ({ status }: { status: string }) => {
  const statusLower = status.toLowerCase();
  const theme = useTheme();
  
  return (
    <Chip
      label={status}
      size="small"
      icon={statusLower === 'success' ? 
        <CheckCircleIcon /> : 
        statusLower === 'failed' ? 
        <ErrorIcon /> : 
        <InfoIcon />
      }
      color={
        statusLower === 'success' ? 'success' : 
        statusLower === 'failed' ? 'error' : 
        'info'
      }
      sx={{ 
        fontWeight: 600,
        px: 1,
        minWidth: 100,
        '& .MuiChip-icon': {
          color: statusLower === 'success' ? 
            theme.palette.success.light : 
            statusLower === 'failed' ? 
            theme.palette.error.light : 
            theme.palette.info.light
        }
      }}
    />
  );
};

const LiveStatusTab = () => {
  const theme = useTheme();
  const authContext = JSON.parse(localStorage.authContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [jobSummary, setJobSummary] = useState<historicalDataLiveStatusSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [paginationStates, setPaginationStates] = useState<{ [key: number]: number }>({});

  const handleChangePage = (groupIndex: number, newPage: number) => {
    setPaginationStates((prev) => ({
      ...prev,
      [groupIndex]: newPage,
    }));
  };
  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response: historicalDataLiveStatusSummary = await fetchHistoricalLiveSummary(authContext.env, authContext.user.lob);
      console.log(JSON.stringify(response));
      if (response) {
        setJobSummary(response);
      } else {
        setError("No data received from server");
      }
    } catch (error) {
      console.error("Failed to fetch historical sync summary:", error);
      setError("Failed to fetch data. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  }, [authContext.env, authContext.user.lob]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  if (isLoading) return <Loader />;

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="error" variant="h6">{error}</Typography>
      </Box>
    );
  }

  if (!jobSummary) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6">No historical sync data available</Typography>
      </Box>
    );
  }

  const hasLiveStatus = jobSummary.liveStatus?.length > 0;
  const hasTableWise = jobSummary.tableWise?.length > 0;

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={4}>
        {/* Live Status Summary Section */}
        <Box>
          <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
            Live Status Summary
          </Typography>

          {hasLiveStatus ? (
            <TableContainer 
              component={Paper} 
              sx={{ 
                borderRadius: 2, 
                maxHeight: 400, 
                overflow: 'auto',
                boxShadow: theme.shadows[3]
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {["Table Name", "Last Sync", "Total Records", "Last Sync Records", "Sync Status"].map((header) => (
                      <TableCell
                      key={header}
                      sx={{ 
                        backgroundColor: '#f0fbff',
                        color: '#227c9d',
                        fontWeight: 600,
                        fontSize: '14px',
                        padding: theme.spacing(2)
                      }}
                    >
                      {header}
                    </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobSummary.liveStatus.map((status, index) => (
                    <StyledTableRow key={index}>
                      <TableCell>{status.tableName}</TableCell>
                      <TableCell>{new Date(status.lastSyncDateTime).toLocaleString()}</TableCell>
                      <TableCell>{status.totalRecordCount}</TableCell>
                      <TableCell>{status.lastSyncRecordCount}</TableCell>
                      <TableCell>
                        <StatusBadge status={status.lastSyncStatus} />
                      </TableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Card 
              elevation={4} 
              sx={{ 
                p: 4, 
                textAlign: 'center', 
                borderRadius: 3,
                backgroundColor: theme.palette.grey[50]
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No Live Status Data Available
              </Typography>
            </Card>
          )}
        </Box>

        {/* Table Wise Summary Section */}
        <Box>
  <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
    Date Wise Summary
  </Typography>

  {hasTableWise ? (
    <Grid container spacing={4}>
      {jobSummary.tableWise.map((group, groupIndex) => {
        const parsedItems = JSON.parse(group.groupItems);
        const page = paginationStates[groupIndex] || 1;
        const rowsPerPage = 10;
        const pageCount = Math.ceil(parsedItems.length / rowsPerPage);
        const paginatedData = parsedItems.slice((page - 1) * rowsPerPage, page * rowsPerPage);

        return (
          <Grid item xs={12} key={groupIndex}>
            <Paper
              elevation={3}
              sx={{
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                p: 2,
              }}
            >
              <Typography variant="h6" fontWeight={600} color="text.secondary" gutterBottom>
                Date: {group.taskDate}
              </Typography>

              <TableContainer
                sx={{
                  borderRadius: 2,
                  maxHeight: 440,
                  overflow: "auto",
                  boxShadow: theme.shadows[1],
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      {["Table Name", "Record Count", "Task Date", "Status", "Failed Reason"].map((header) => (
                       <TableCell
                        key={header}
                        sx={{ 
                          backgroundColor: '#f0fbff',
                          color: '#227c9d',
                          fontWeight: 600,
                          fontSize: '14px',
                          padding: theme.spacing(2)
                        }}
                      >
                        {header}
                      </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.map((record: any, recordIndex: number) => (
                      <StyledTableRow key={recordIndex}>
                        <TableCell>{record.table_name}</TableCell>
                        <TableCell>{record.recordcount}</TableCell>
                        <TableCell>{new Date(record.taskdate).toLocaleString()}</TableCell>
                        <TableCell>
                          <StatusBadge status={record.status} />
                        </TableCell>
                        <TableCell>
                          {record.failedreason ? (
                            <Chip
                              label={record.failedreason}
                              color="error"
                              size="small"
                              sx={{ maxWidth: 200 }}
                            />
                          ) : (
                            "NA"
                          )}
                        </TableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {parsedItems.length > rowsPerPage && (
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Pagination
                    count={pageCount}
                    page={page}
                    onChange={(_, value) => handleChangePage(groupIndex, value)}
                    color="primary"
                    size="small"
                  />
                </Box>
              )}
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  ) : (
    <Card
      elevation={4}
      sx={{
        p: 4,
        textAlign: "center",
        borderRadius: 3,
        backgroundColor: theme.palette.grey[50],
      }}
    >
      <Typography variant="body1" color="text.secondary">
        No Date Wise Summary Available
      </Typography>
    </Card>
  )}
</Box>



      </Stack>
    </Box>
  );
};

export default LiveStatusTab;